<template>
  <div>
    <!-- Begin page -->
    <div id="layout-wrapper">
      <NavigationBar />

      <!-- Main Content -->
      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">
            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Manage Leave</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="/">Home</a></li>
                      <li class="breadcrumb-item active">Leave & Attendance</li>
                      <li class="breadcrumb-item active">Leave</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Stats Cards -->
            <div class="row">
              <div class="col-lg-3 col-md-6" v-for="stat in statsCards" :key="stat.key">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex flex-wrap">
                      <div class="me-3">
                        <p class="text-muted mb-2">{{ stat.label }}</p>
                        <h5 class="mb-0">{{ metrics[stat.key] || 0 }}</h5>
                      </div>
                      <div class="avatar-sm ms-auto">
                        <div :class="`avatar-title ${stat.bgColor} rounded-circle text-white font-size-20`">
                          <i :class="`mdi ${stat.icon}`"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Table Card -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <!-- Card Header with Filters -->
                  <div class="card-header bg-white border-bottom">
                    <div class="d-flex justify-content-between align-items-center">
                      <!-- Search Box -->
                      <div class="search-box me-3" style="min-width: 300px;">
                        <div class="position-relative">
                          <input
                              v-model="searchValue"
                              type="text"
                              class="form-control bg-light border-light rounded"
                              placeholder="Search by employee name..."
                              @keyup.enter="getLeaveRequests"
                          >
                          <i class="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>

                      <!-- Filter Controls -->
                      <div class="d-flex align-items-center">
                        <!-- Apply Leave Button -->
                        <button
                            class="btn btn-success me-3"
                            data-bs-toggle="modal"
                            data-bs-target="#leave-application-modal"
                        >
                          <i class="mdi mdi-plus-circle font-size-16"></i>
                          <span class="ms-1">Apply Leave</span>
                        </button>

                        <!-- Status Filter -->
                        <select
                            class="form-select me-3"
                            style="width: 200px;"
                            v-model="status"
                            @change="getLeaveRequests"
                        >
                          <option value="">All Applications</option>
                          <option value="Pending">Pending</option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                        </select>

                        <!-- Download Button -->
                        <button
                            @click="downloadLeaveRequests"
                            class="btn btn-primary"
                            :disabled="downloadLoading"
                        >
                          <i class="bx bxs-download font-size-16"></i>
                          <span class="ms-1">{{ downloadText }}</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Table Body -->
                  <div class="card-body">
                    <div v-if="loading" class="text-center py-5">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading leave requests...</p>
                    </div>

                    <div v-else-if="leaveRequests.length === 0" class="text-center py-5">
                      <i class="mdi mdi-file-document-outline" style="font-size: 3rem; color: #ccc;"></i>
                      <p class="mt-2 text-muted">No leave requests found</p>
                    </div>

                    <div v-else class="table-responsive">
                      <table class="table align-middle table-hover table-nowrap">
                        <thead class="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Leave Type</th>
                          <th>Application Date</th>
                          <th>Duration</th>
                          <th>Days</th>
                          <th>Reason</th>
                          <th>Status</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in paginatedLeaveRequests" :key="item.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-xs me-3">
                                  <span class="avatar-title rounded-circle bg-soft-primary text-primary">
                                    {{ getInitials(item.names) }}
                                  </span>
                              </div>
                              <div>
                                <p class="mb-0 fw-medium">{{ item.names }}</p>
                              </div>
                            </div>
                          </td>
                          <td>{{ item.leaveTypeName }}</td>
                          <td>{{ formatDate(item.createdAt) }}</td>
                          <td>
                            <small class="text-muted">
                              {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
                            </small>
                          </td>
                          <td>
                            <span class="badge bg-soft-info text-info">{{ item.totalDays }} Days</span>
                          </td>
                          <td>
                              <span class="d-inline-block text-truncate" style="max-width: 200px;" :title="item.reason">
                                {{ item.reason }}
                              </span>
                          </td>
                          <td>
                              <span
                                  class="badge rounded-pill"
                                  :class="getStatusClass(item.status)"
                              >
                                {{ item.status }}
                              </span>
                          </td>
                          <td>
                            <div class="d-flex justify-content-center gap-1">
                              <button
                                  @click="showConfirmationModal(item, 'Approved')"
                                  class="btn btn-sm btn-success"
                                  :disabled="item.processing"
                                  title="Approve"
                              >
                                <i class="mdi mdi-check"></i>
                              </button>
                              <button
                                  @click="showConfirmationModal(item, 'Rejected')"
                                  class="btn btn-sm btn-danger"
                                  :disabled="item.processing"
                                  title="Reject"
                              >
                                <i class="mdi mdi-close"></i>
                              </button>
                              <button
                                  @click="viewDetails(item)"
                                  class="btn btn-sm btn-info"
                                  data-bs-target=".leave-details-modal"
                                  data-bs-toggle="modal"
                                  title="View Details"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing {{ startIndex + 1 }} to {{ Math.min(endIndex, totalItems) }} of {{ totalItems }} entries
                      </div>
                      <nav>
                        <ul class="pagination mb-0">
                          <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <button class="page-link" @click="prevPage">Previous</button>
                          </li>
                          <li
                              v-for="page in visiblePages"
                              :key="page"
                              class="page-item"
                              :class="{ active: page === currentPage, disabled: page === '...' }"
                          >
                            <button
                                class="page-link"
                                @click="page !== '...' && goToPage(page)"
                            >
                              {{ page }}
                            </button>
                          </li>
                          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <button class="page-link" @click="nextPage">Next</button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <LeaveDetailsModal :leave="selectedLeaveRequest"/>
    <LeaveApplicationModal
        :employee-id="currentEmployeeId"
        @leave-submitted="onLeaveSubmitted"
    />
    <ApprovalConfirmationModal
        ref="approvalModal"
        :leave-request="pendingApproval.leaveRequest"
        :action-type="pendingApproval.action"
        @confirm="handleApprovalConfirmation"
    />
    <SuccessModal
        title="Leave Approval"
        heading="Leave Successfully Processed!"
        :message="successMessage"
    />
    <ErrorModal
        title="Action Failed"
        heading="Operation Failed"
        :message="errorMessage"
    />
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import LeaveDetailsModal from "./modals/LeaveDetailsModal.vue";
import SuccessModal from "./modals/SuccessModal.vue";
import ErrorModal from "./modals/ErrorModal.vue";
import LeaveApplicationModal from "./modals/LeaveApplicationModal.vue";
import ApprovalConfirmationModal from "./modals/ApprovalConfirmationModal.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "AttendanceLeave",
  components: {
    NavigationBar,
    LeaveDetailsModal,
    SuccessModal,
    ErrorModal,
    LeaveApplicationModal,
    ApprovalConfirmationModal
  },
  data() {
    return {
      // Loading states
      loading: false,
      downloadLoading: false,

      // Messages
      successMessage: '',
      errorMessage: '',

      // Filter and search
      searchValue: "",
      status: '',
      downloadText: 'Download',

      // Pagination
      currentPage: 1,
      totalPages: 0,
      pageSize: 10,
      totalItems: 0,

      // Data
      leaveRequests: [],
      selectedLeaveRequest: {},
      currentEmployeeId: '', // This should be set from user session/auth

      // Approval confirmation tracking
      pendingApproval: {
        leaveRequest: {},
        action: 'Approved'
      },

      metrics: {
        totalOnLeave: 0,
        pendingRequests: 0,
        approvedRequests: 0,
        rejectedRequests: 0
      },

      // Stats configuration
      statsCards: [
        {
          key: 'totalOnLeave',
          label: 'Total Employees on Leave',
          icon: 'mdi-airplane',
          bgColor: 'bg-primary'
        },
        {
          key: 'pendingRequests',
          label: 'Pending Requests',
          icon: 'mdi-clock-alert',
          bgColor: 'bg-warning'
        },
        {
          key: 'approvedRequests',
          label: 'Approved Requests',
          icon: 'mdi-check-circle',
          bgColor: 'bg-success'
        },
        {
          key: 'rejectedRequests',
          label: 'Rejected Requests',
          icon: 'mdi-close-circle',
          bgColor: 'bg-danger'
        }
      ]
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.pageSize;
    },
    endIndex() {
      return this.currentPage * this.pageSize;
    },
    paginatedLeaveRequests() {
      return this.leaveRequests;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      if (start > 1) {
        pages.push(1);
        if (start > 2) pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages) {
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }

      return pages;
    }
  },
  mounted() {
    this.getLeaveRequests();
  },
  methods: {
    // Format date helper
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const options = { day: 'numeric', month: 'short', year: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    // Get initials from name
    getInitials(name) {
      if (!name) return 'NA';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    // Get status badge class
    getStatusClass(status) {
      const classes = {
        'Pending': 'bg-warning',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    // Fetch leave requests
    async getLeaveRequests() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getLeaveRequests");
      data.append("page", this.currentPage);
      data.append("rows_per_page", this.pageSize);
      data.append("search", this.searchValue);
      data.append("status", this.status);

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.leaveRequests = res.data.data.leaveRequests.map(item => ({
            ...item,
            processing: false
          }));
          this.metrics = res.data.data.metrics;
          this.totalItems = res.data.data.totalCount || this.leaveRequests.length;
          this.totalPages = Math.ceil(this.totalItems / this.pageSize);
        }
      } catch (error) {
        console.error('Error fetching leave requests:', error);
        this.errorMessage = 'Failed to load leave requests';
        $('#error-modal').modal('show');
      } finally {
        this.loading = false;
      }
    },

    // Download leave requests
    async downloadLeaveRequests() {
      this.downloadLoading = true;
      this.downloadText = "Downloading...";

      const data = new FormData();
      data.append("function", "downloadLeaveRequests");
      data.append("search", this.searchValue);
      data.append("status", this.status);

      try {
        const response = await executeDownload(data, authUrl);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(response.data);
        link.download = `LeaveReport_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Download error:', error);
        this.errorMessage = 'Failed to download report';
        $('#error-modal').modal('show');
      } finally {
        this.downloadLoading = false;
        this.downloadText = "Download";
      }
    },

    // Show confirmation modal before approve/reject
    showConfirmationModal(item, action) {
      this.pendingApproval = {
        leaveRequest: { ...item },
        action: action
      };

      // Show the modal using Bootstrap's modal API
      $('#approval-confirmation-modal').modal('show');
    },

    // Handle the approval confirmation from modal
    async handleApprovalConfirmation(confirmationData) {
      const { leaveRequest, action, remarks } = confirmationData;

      const itemIndex = this.leaveRequests.findIndex(l => l.id === leaveRequest.id);
      if (itemIndex !== -1) {
        this.leaveRequests[itemIndex].processing = true;
      }

      const data = new FormData();
      data.append("function", "approveOrRejectLeave");
      data.append("leaveRequestId", leaveRequest.id);
      data.append("action", action);
      data.append("remarks", remarks);

      // Add these if you have user session data
      // data.append("approvedBy", this.currentUserName);
      // data.append("approvedById", this.currentUserId);

      try {
        const res = await execute(data, authUrl);

        // Hide the confirmation modal
        $('#approval-confirmation-modal').modal('hide');

        // Reset the modal processing state
        if (this.$refs.approvalModal) {
          this.$refs.approvalModal.resetProcessing();
        }

        if (res.data.success) {
          this.successMessage = res.data.message || `Leave request ${action.toLowerCase()} successfully`;
          $('#success-modal').modal('show');
          await this.getLeaveRequests();
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        // Hide the confirmation modal
        $('#approval-confirmation-modal').modal('hide');

        // Reset the modal processing state
        if (this.$refs.approvalModal) {
          this.$refs.approvalModal.resetProcessing();
        }

        this.errorMessage = error.message || `Failed to ${action.toLowerCase()} leave request`;
        $('#error-modal').modal('show');
      } finally {
        if (itemIndex !== -1) {
          this.leaveRequests[itemIndex].processing = false;
        }
      }
    },

    // View details
    viewDetails(item) {
      this.selectedLeaveRequest = { ...item };
    },

    // Pagination methods
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getLeaveRequests();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getLeaveRequests();
      }
    },

    goToPage(page) {
      if (page > 0 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getLeaveRequests();
      }
    },

    // Handle successful leave submission
    onLeaveSubmitted(data) {
      // Refresh the leave requests list
      this.getLeaveRequests();

      // Show success notification
      this.successMessage = 'Your leave request has been submitted successfully and is pending approval.';
      setTimeout(() => {
        $('#success-modal').modal('show');
      }, 500);
    }
  }
};
</script>

<style scoped>
.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #74788d;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2rem;
  width: 2rem;
}

.bg-soft-primary {
  background-color: rgba(85, 110, 230, 0.1);
}

.bg-soft-info {
  background-color: rgba(52, 195, 143, 0.1);
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.table th {
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  color: #495057;
}
</style>